import { useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { Domain } from "../../../api/Api";
import { apiClient } from "../../../api/ApiClient";
import { Icon } from "../../../assets/icons/Icon";
import { useWizard } from "../../../contexts/WizardContext";
import Button from "../../mantine/button/Button";
import Input from "../../ui/input/Input";
import Spinner from "../../ui/spinner/Spinner";
import { routingConfig } from "../helpers";
import styles from "./Wizzard.module.scss";

type RoutingProps = {
  isWizzard?: boolean;
  domain?: Domain | undefined;
};

const Routing = ({ isWizzard, domain }: RoutingProps) => {
  const { domainId } = useParams<{ domainId: string }>();
  const { navigateToPrevStep, updateDomain } = useWizard();
  const [isPolling, setIsPolling] = useState(true);
  const [isPollingDomain, setIsPollingDomain] = useState(true);
  const [copiedValues, setCopiedValues] = useState<{ [key: string]: boolean }>(
    {},
  );

  useEffect(() => {
    if (!isPolling)
      enqueueSnackbar("Domain configuration complete!", { variant: "success" });
  }, [isPolling]);

  // Here we need to use the special statusDetail to force the
  // status resync at the backend.
  const { data: domainStatus } = useQuery({
    queryKey: ["domainStatus", domainId],
    queryFn: async () => {
      setIsPollingDomain(true);
      const response = await apiClient.domains.statusDetail(Number(domainId));
      setIsPollingDomain(false);

      return response?.data?.domain.status || {};
    },

    refetchOnWindowFocus: false,
    refetchInterval: isPolling ? 20000 : false,
    refetchOnMount: true,
  });

  const { data: routingHostsStateDetail } = useQuery({
    enabled: !isPollingDomain,
    queryKey: ["routingHostsStateDetail", domainId],
    queryFn: async () => {
      const response = await apiClient.domains.routingHostsStateDetail(
        Number(domainId),
      );

      setIsPolling(
        !domainStatus?.routing || !response?.data?.routing_hosts_state,
      );

      return response?.data?.routing_hosts_state;
    },
    refetchInterval: isPolling ? 20000 : false,
    refetchOnWindowFocus: false,
  });

  const handleContinue = async () => {
    if (isWizzard) {
      // Update Domain state
      const result = await apiClient.domains.domainsDetail(Number(domainId));
      const updatedDomain = result?.data?.domain;

      if (updatedDomain) updateDomain(updatedDomain);
    }
  };

  const handleBack = () => {
    navigateToPrevStep();
  };

  const handleCopy = (value: string) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedValues((prev) => ({
          ...prev,
          [value]: true,
        }));

        setTimeout(() => {
          setCopiedValues((prev) => ({
            ...prev,
            [value]: false,
          }));
        }, 2000);
      })
      .catch(() => {
        enqueueSnackbar("Unexpected error", { variant: "error" });
      });
  };

  const getMessage = (routingKey: string): string => {
    switch (routingKey) {
      case "cname":
        return "No <strong>CNAME</strong> records found";
      case "aaaa":
        return "No <strong>AAAA</strong> records found";
      case "a":
        return "Correct <strong>A</strong> records set";
      default:
        return "";
    }
  };

  const statusIcons: Record<
    "true" | "false" | "null",
    "GreenCheckmarkIcon" | "GreyCheckmarkIcon" | "GreyCrossmarkIcon"
  > = {
    true: "GreenCheckmarkIcon",
    false: "GreyCheckmarkIcon",
    null: "GreyCrossmarkIcon",
  };

  const renderRoutingHostsStateDetail = (
    routingKey: string,
    active: boolean | null,
  ) => {
    const message = getMessage(routingKey);

    const iconName = statusIcons[String(active) as "true" | "false" | "null"];

    return (
      <div className={styles.statusItem}>
        <Icon
          iconName={iconName}
          // @ts-expect-error TS2339 non-existing className
          className={styles.statusIcon}
          width="1rem"
          height="1rem"
        />
        <span
          // @ts-expect-error TS2551 non-existing className
          className={styles.statusText}
          dangerouslySetInnerHTML={{ __html: message }}
        />
      </div>
    );
  };

  const pollingStatus = isPolling && routingHostsStateDetail && (
    <Spinner size="1rem" className={styles.pollingStatus} />
  );

  return (
    // @ts-expect-error TS2339 non-existing className
    <div className={styles.stepContent}>
      <div className={styles.stepContentTitle}>Routing</div>
      <div className={styles.stepContentWrapper}>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.stepContentSection}>
          <div className={styles.stepContentListText}>
            In order to re-route the traffic via the AI Visibility Engine Proxy,
            go to your domain registrar or authoritative DNS provider, and find
            the DNS management console.
          </div>
          <div>
            <ol className={styles.stepContentList}>
              <li>
                Remove CNAME records, if any, for hosts from the list above.
              </li>
              <li>
                Remove AAAA records, if any, for hosts from the list above.
              </li>
              <li>Set A records for the following hosts:</li>
            </ol>
          </div>
          {/*
           // @ts-expect-error TS2551 non-existing className */}
          <div className={styles.stepContentSection}>
            {domain?.sites && domain.sites.length > 0 && (
              // @ts-expect-error TS2339 non-existing className
              <div className={styles.sitesContainer}>
                {domain.sites
                  .filter((site) => site.prerendering_enabled)
                  .map((site) => (
                    <div key={site.id} className={styles.siteItem}>
                      {site.host_names && site.host_names.length > 0 && (
                        <ul className={styles.hostNamesList}>
                          {site.host_names.map((hostname, index) => (
                            // @ts-expect-error TS2339 non-existing className
                            <li key={index} className={styles.hostItem}>
                              <div className={styles.hostItemTitle}>
                                {hostname}
                              </div>
                              <div className={styles.routingConfigContainer}>
                                {routingConfig.map((record, index) => (
                                  <div key={index} className={styles.inputRow}>
                                    <div className={styles.inputWithLabel}>
                                      <div
                                        // @ts-expect-error TS2551 non-existing className
                                        className={styles.inputWithLabelTitle}
                                      >
                                        Type
                                      </div>
                                      <div
                                        className={styles.inputWithLabelInput}
                                      >
                                        <Input
                                          type="text"
                                          value={record.type}
                                          readOnly
                                          ownProps={{
                                            noErrors: true,
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <div className={styles.inputWithLabel}>
                                      <div
                                        // @ts-expect-error TS2551 non-existing className
                                        className={styles.inputWithLabelTitle}
                                      >
                                        Value
                                      </div>
                                      <Input
                                        type="text"
                                        value={record.value}
                                        readOnly
                                        ownProps={{
                                          noErrors: true,
                                        }}
                                      />
                                    </div>
                                    <div className={styles.inputWithLabel}>
                                      <div
                                        // @ts-expect-error TS2551 non-existing className
                                        className={styles.inputWithLabelTitle}
                                      >
                                        TTL*
                                      </div>
                                      <Input
                                        type="text"
                                        value={record.ttl}
                                        readOnly
                                        ownProps={{
                                          noErrors: true,
                                        }}
                                      />
                                    </div>
                                    <Button
                                      variant="outlined"
                                      onClick={() => handleCopy(record.value)}
                                    >
                                      {copiedValues[record.value] ? (
                                        <>
                                          <Icon iconName="CheckmarkIcon" />
                                          <span>Copied!</span>
                                        </>
                                      ) : (
                                        <>
                                          <Icon iconName="CopyIcon" />
                                          <span>Copy IP</span>
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
            )}
            <p>
              * Using a low TTL value, from 60 to 300 seconds, is recommended
              while testing. You can increase it later on.
            </p>
          </div>
        </div>
        {/*
         // @ts-expect-error TS2551 non-existing className */}
        <div className={styles.stepContentSection}>
          {/*
           // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.stepRightSection}>
            {/*
             // @ts-expect-error TS2339 non-existing className */}
            <div className={styles.sectionTitleContainer}>
              <div className={styles.sectionTitle}>
                <div className={styles.certificateTitle}>
                  {pollingStatus} AI Visibility Engine Status
                </div>
              </div>
            </div>

            {routingHostsStateDetail &&
              Object.entries(routingHostsStateDetail).map(
                ([domain, records]) => (
                  // @ts-expect-error TS2339 non-existing className
                  <div key={domain} className={styles.statusContainer}>
                    <div className={styles.domainHeader}>{domain}</div>
                    <ul className={styles.statusList}>
                      {records && typeof records === "object"
                        ? Object.entries(records).map(
                            ([recordType, status]) => (
                              <li
                                key={recordType}
                                className={styles.statusItem}
                              >
                                {renderRoutingHostsStateDetail(
                                  recordType,
                                  status as boolean | null,
                                )}
                              </li>
                            ),
                          )
                        : null}
                    </ul>
                  </div>
                ),
              )}
          </div>
        </div>
      </div>
      {isWizzard && (
        <div className={styles.stepContentFooter}>
          {isWizzard && (
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
          )}
          <Button onClick={handleContinue} disabled={isPolling}>
            {isPolling && <Spinner size="1rem" />} Finish
          </Button>
        </div>
      )}
    </div>
  );
};
export default Routing;
